import React from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import postsData from "../data/blogPosts.json";
import "./blogPost.css";

/* helpers */
const toSlug = (s = "") =>
  s.toLowerCase().replace(/[^a-z0-9\s-]/g, "").trim().replace(/\s+/g, "-");

const getTagLabel = (t) => (typeof t === "string" ? t : t?.name || "");
const getTagColor = (t) => (typeof t === "string" ? "#a78bfa" : t?.color || "#a78bfa");

const TagPill = ({ tag }) => (
  <span className="tag-pill" style={{ background: getTagColor(tag) }}>
    {getTagLabel(tag)}
  </span>
);

/* Detail page at /blog/:slug */
export const BlogPost = () => {
  const { id: slugParam } = useParams();
  const navigate = useNavigate();

  const post =
    postsData.find((p) => (p.slug || toSlug(p.title)) === slugParam) || null;

  if (!post) {
    return (
      <div className="blogpost" style={{ padding: "120px 20px" }}>
        <div style={{ maxWidth: 800, margin: "0 auto" }}>
          <p style={{ marginBottom: 16 }}>Post not found.</p>
          <button className="back-btn" onClick={() => navigate("/blog")}>
            ← Back to Blog
          </button>
        </div>
      </div>
    );
  }

  // 🗓️ Format date
  const formattedDate = post.date
    ? new Date(post.date).toLocaleString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : null;

  return (
    <div className="blogpost">
      {/* ---------- Navbar (unchanged) ---------- */}
      <nav id="menu" className="navbar navbar-default navbar-fixed-top">
        <div className="container">
          <div className="navbar-header">
            <button
              type="button"
              className="navbar-toggle collapsed"
              data-toggle="collapse"
              data-target="#bs-example-navbar-collapse-1"
            >
              <span className="sr-only">Toggle navigation</span>
              <span className="icon-bar"></span>
              <span className="icon-bar"></span>
              <span className="icon-bar"></span>
            </button>

            <a
              className="navbar-brand page-scroll"
              href="#page-top"
              style={{ display: "flex", alignItems: "center" }}
            >
              <img
                src="./img/cradleai_logo.png"
                alt="CradleAI Logo"
                style={{ height: "40px", display: "inline-block" }}
              />
            </a>
          </div>

          <div className="collapse navbar-collapse" id="bs-example-navbar-collapse-1">
            <ul className="nav navbar-nav navbar-right">
              <li>
                <Link to="/" className="page-scroll" replace>
                  Home
                </Link>
              </li>
              <li>
                <Link to="/blog" className="nav-link" data-router>
                  Blog
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </nav>

      {/* ---------- Hero Card ---------- */}
      <section className="post-hero">
        <div className="card">
          <img
            className="media postImage"
            src={post.image}
            alt={post.title}
            onError={(e) => (e.currentTarget.src = "./img/blog/placeholder.jpg")}
          />
          <div className="body">
            <h1>{post.title}</h1>
            <div className="meta">
              {post.writtenBy && (
                <span>
                  <strong>{post.writtenBy}</strong>
                </span>
              )}
              {formattedDate && <span>{formattedDate}</span>}
              {/* tags preview inline (short) */}
              {Array.isArray(post.tags) && post.tags.length > 0 && (
                <div className="tags" style={{ marginLeft: "auto" }}>
                  {post.tags.slice(0, 3).map((tag, i) => (
                    <TagPill key={i} tag={tag} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ---------- Main + Aside ---------- */}
      <section className="post-wrap">
        {/* Article */}
        <article className="post-article">
          <div className="content">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {post.content}
            </ReactMarkdown>
          </div>
        </article>

        {/* Sticky aside */}
        <aside className="post-aside">
          <div className="card">
            <h4>Article Info</h4>
            <div className="item">
              <span>Author</span>
              <span>{post.writtenBy || "—"}</span>
            </div>
            <div className="item">
              <span>Published</span>
              <span>{formattedDate || "—"}</span>
            </div>
            <div className="item" style={{ display: "block" }}>
              <h4 style={{ marginTop: 12, marginBottom: 8 }}>Tags</h4>
              <div className="tags">
                {post.tags?.map((tag, i) => (
                  <TagPill key={i} tag={tag} />
                ))}
              </div>
            </div>

            <button className="back-btn" onClick={() => navigate("/blog")}>
              ← Back to Blog
            </button>
          </div>
        </aside>
      </section>
    </div>
  );
};
